import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { RefreshCw, Trash2 } from 'lucide-react';
import { useFetch } from '../hooks/useFetchAuth';
import { useLoadActions } from '../hooks/useLoadActions';
import ReprocessDialog from './ReprocessDialog';

const JOB_POLL_MS = 3000;

// Reprocess or delete from the load detail page. The page is refreshed when the job ends: a
// deleted sheet then shows as deleted, a reprocessed load links to its new entry.
function LoadActions({ uuid }) {
  const queryClient = useQueryClient();
  const { data } = useFetch(['history-load', uuid], `/api/history/${uuid}/load`);
  const { deleteSheet, reprocessLoad, deletingUuid, reprocessingUuid } = useLoadActions();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [job, setJob] = useState(null);

  const { data: jobData } = useFetch(['load-action-job', job?.id], `/api/process/jobs/?ids=${job?.id}`, {
    enabled: Boolean(job?.id),
    refetchInterval: (query) => (query.state.data?.jobs?.[0]?.finished_at ? false : JOB_POLL_MS),
  });
  const finished = Boolean(jobData?.jobs?.[0]?.finished_at);

  useEffect(() => {
    if (!finished) return;
    ['history-load', 'history-mapping', 'history-data', 'history-files'].forEach((key) =>
      queryClient.invalidateQueries({ queryKey: [key, uuid] })
    );
    queryClient.invalidateQueries({ queryKey: ['history'] });
    setJob(null);
  }, [finished, uuid, queryClient]);

  if (!data) return null;
  const { load } = data;
  const item = { ...load, uuid };

  if (load.deleted_at) {
    return load.replaced_by ? (
      <Link
        to={`/processor/history/${load.replaced_by}`}
        className="inline-flex items-center px-3 py-1.5 text-sm rounded bg-blue-500 text-white hover:bg-blue-600"
      >
        Voir le chargement actuel
      </Link>
    ) : null;
  }

  const running = Boolean(job) || deletingUuid === uuid || reprocessingUuid === uuid;
  const otherSheets = data.sheets.length - 1;

  const handleDelete = async () => {
    const jobId = await deleteSheet(
      item,
      `Supprimer la feuille « ${load.sheet} » de ${load.filename} (S${load.week} ${load.year}) ? ` +
        'Ses données seront retirées de Snowflake' +
        (otherSheets > 0 ? `, les ${otherSheets} autre(s) feuille(s) de ce chargement restent chargées.` : '.')
    );
    if (jobId) setJob({ id: jobId, label: 'Suppression en cours…' });
  };

  const handleReprocess = async (sheetsOverride) => {
    setDialogOpen(false);
    const jobId = await reprocessLoad(item, sheetsOverride);
    if (jobId) setJob({ id: jobId, label: 'Retraitement en cours…' });
  };

  return (
    <div className="flex items-center gap-2">
      {job ? (
        <span className="inline-flex items-center gap-2 text-sm text-gray-600 dark:text-gray-300">
          <span className="w-4 h-4 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
          {job.label}
        </span>
      ) : (
        <>
          <button
            type="button"
            onClick={() => setDialogOpen(true)}
            disabled={running}
            className="inline-flex items-center gap-2 px-3 py-1.5 text-sm rounded border border-orange-300 text-orange-700 hover:bg-orange-50 disabled:opacity-50 dark:border-orange-700 dark:text-orange-300 dark:hover:bg-orange-900/30"
          >
            <RefreshCw className="w-4 h-4" />
            Retraiter
          </button>
          <button
            type="button"
            onClick={handleDelete}
            disabled={running}
            className="inline-flex items-center gap-2 px-3 py-1.5 text-sm rounded border border-red-300 text-red-700 hover:bg-red-50 disabled:opacity-50 dark:border-red-700 dark:text-red-300 dark:hover:bg-red-900/30"
          >
            <Trash2 className="w-4 h-4" />
            Supprimer cette feuille
          </button>
        </>
      )}
      {dialogOpen && (
        <ReprocessDialog item={item} onCancel={() => setDialogOpen(false)} onConfirm={handleReprocess} />
      )}
    </div>
  );
}

export default LoadActions;
